import type { FormChannel } from "../channels";
import type {
  CallDestination,
  CallRoutingRule,
  Connector,
  ConnectorCapability,
  ConnectorStatus,
  ConnectorType,
  GeoCallFilter,
  OutboundCallFilter,
  SipNumberFormat,
  SipTransportProtocol,
} from "@/features/connectors/types";

/** The SIP form's own fields, as typed (port kept as text until submit). */
export interface SipRegistrationForm {
  server: string;
  port: string;
  transport: SipTransportProtocol;
  username: string;
  authUsername: string;
  /** Empty on edit keeps the stored password. */
  password: string;
  outboundPrefix: string;
  numberFormat: SipNumberFormat;
}

export const emptySipRegistrationForm = (): SipRegistrationForm => ({
  server: "",
  port: "",
  transport: "udp",
  username: "",
  authUsername: "",
  password: "",
  outboundPrefix: "",
  numberFormat: "national",
});

/** One server of the mailbox, as typed (port kept as text until submit). */
export interface MailServerForm {
  host: string;
  port: string;
  /** TLS from the first byte; off means STARTTLS, never plain text. */
  secure: boolean;
  /** Empty = the mailbox address. */
  username: string;
  /** Empty on edit keeps the stored password. */
  password: string;
}

/** The `email` connector's mailbox, as typed. */
export interface EmailAccountForm {
  address: string;
  displayName: string;
  smtp: MailServerForm;
  imap: MailServerForm;
  /** IMAP folder; empty = INBOX. */
  mailbox: string;
  /** Copy answers into Sent. `null` = automatic (off where the provider files its own copy). */
  saveToSent: boolean | null;
  /** The Sent folder when the server does not flag it; empty = detected. */
  sentFolder: string;
}

const emptyMailServerForm = (): MailServerForm => ({
  host: "",
  port: "",
  secure: true,
  username: "",
  password: "",
});

export const emptyEmailAccountForm = (): EmailAccountForm => ({
  address: "",
  displayName: "",
  smtp: emptyMailServerForm(),
  imap: emptyMailServerForm(),
  mailbox: "",
  saveToSent: null,
  sentFolder: "",
});

export interface ConnectorFormProps {
  mode: "create" | "edit";
  connector?: Connector;
  /**
   * Create only: which channel the client chose in the picker. The form then
   * offers that channel's types and nothing else — and no type field at all when
   * it has just one (an e-mail connector, a web chat).
   */
  channel?: FormChannel;
  onSubmit: (data: ConnectorFormData) => void | Promise<void>;
  isSubmitting?: boolean;
}

export interface ConnectorFormData {
  type: ConnectorType;
  status: ConnectorStatus;
  capabilities: ConnectorCapability[];
  agentId: string;
  accountId: string;
  authToken: string;
  /** Twilio Region code ("ie1", …); empty = the default "us1". */
  twilioRegion: string;
  phoneNumbers: string;
  whatsappNumbers: string;
  routingRules: CallRoutingRule[];
  geoFilter: GeoCallFilter;
  /** Which countries this line may call out to. See OutboundCallFilter. */
  outboundFilter: OutboundCallFilter;
  /** Where callers go when we cannot serve the call, in order. Empty = drop. */
  fallbackDestinations: CallDestination[];
  /** Schedule during which the agent is off and calls go to the fallback. Empty = never. */
  agentOffScheduleId: string;
  /** Seconds the team rings before the agent takes the call. 0 = never. */
  ringBeforeAgentSeconds: number;
  allowedOrigins: string;
  /** Comma-separated caller numbers of a "temporary" (demo) connector. */
  callerNumbers: string;
  /** twilio/sip: this number is shared as a demo number (SYSADMIN only). */
  demoNumber: boolean;
  /** A demo number whose agent routes callers to a demo space by code (SYSADMIN only). */
  codeRouting: boolean;
  /** twilio/sip: answers on every number nobody owns (SYSADMIN only). */
  defaultConnector: boolean;
  webCallEnabled: boolean;
  /** Let the tenant's own site ask, unauthenticated, for a visitor to be called back. */
  publicCallbackEnabled: boolean;
  /** `sip` only: the extension HeyUp registers as on the client's PBX. */
  sipRegistration: SipRegistrationForm;
  /** `email` only: the mailbox HeyUp reads and answers from. */
  emailAccount: EmailAccountForm;
}

/**
 * The registration as the API takes it, or nothing for another type. An empty
 * password is left out: the server keeps the stored one.
 */
export const sipRegistrationPayload = (formData: ConnectorFormData) => {
  if (formData.type !== "sip") return undefined;
  const sip = formData.sipRegistration;
  return {
    server: sip.server,
    ...(sip.port ? { port: Number(sip.port) } : {}),
    transport: sip.transport,
    username: sip.username,
    ...(sip.authUsername ? { auth_username: sip.authUsername } : {}),
    ...(sip.password ? { password: sip.password } : {}),
    // Sent even empty: clearing the prefix has to reach the server.
    outbound_prefix: sip.outboundPrefix,
    number_format: sip.numberFormat,
  };
};

const mailServerPayload = (server: MailServerForm) => ({
  host: server.host,
  ...(server.port ? { port: Number(server.port) } : {}),
  secure: server.secure,
  username: server.username,
  // Left out when empty: the server keeps the stored one.
  ...(server.password ? { password: server.password } : {}),
});

/**
 * The mailbox as the API takes it, or nothing for another type. An empty
 * password is left out: the server keeps the stored one.
 */
export const emailAccountPayload = (formData: ConnectorFormData) => {
  if (formData.type !== "email") return undefined;
  const account = formData.emailAccount;
  return {
    address: account.address,
    display_name: account.displayName,
    smtp: mailServerPayload(account.smtp),
    imap: { ...mailServerPayload(account.imap), mailbox: account.mailbox },
    // Absent when left on automatic: the server decides from the provider.
    ...(account.saveToSent === null
      ? {}
      : { save_to_sent: account.saveToSent }),
    sent_folder: account.sentFolder,
  };
};
